import { Clips, type ClipsListResponse, type PublicClip, ShortUrl } from '@air/api';
import {
  type Board,
  type Clip,
  type ClipsListOptions,
  type ListResponse,
  type PublicBoard,
  type SortDirection,
  type SortField,
} from '@air/api/types';

import { PageLimits } from '~/constants/PageLimits';
import { type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';

export const privateClipsFetcher = async ({
  params,
  sortFieldName,
  sortDirection,
  cursor,
  workspaceId,
}: {
  cursor: string | null;
  params: Partial<ClipsListOptions>;
  sortFieldName: SortField['name'];
  sortDirection: SortDirection;
  workspaceId: string;
}) => {
  const response = await Clips.search({
    cursor,
    ...params,
    limit: !cursor ? PageLimits.clips.init : PageLimits.clips.more,
    sortField: {
      direction: sortDirection,
      name: sortFieldName,
    },
    workspaceId,
  });

  return response;
};

/**
 * Scopes a drilled board's clip/file search to the collection it is being viewed within. Without
 * the filter the backend resolves the share link's ROOT collection, so a board drilled into from a
 * collection sub-page would list the root's assets instead of the sub-page's. `scope: 'all'`
 * matters: a drilled board's assets are held by boards, i.e. indirect collection members.
 *
 * `collectionId` absent (the standalone board link and root-collection drills, where the backend's
 * root default is already correct) returns the caller's params untouched, keeping those requests
 * byte-identical.
 */
export const getCollectionScopedClipParams = ({
  params,
  collectionId,
}: {
  params: Partial<ClipsListOptions>;
  collectionId?: string;
}): Partial<ClipsListOptions> => {
  if (!collectionId) {
    return params;
  }

  return {
    ...params,
    filters: {
      ...params.filters,
      collection: { is: collectionId, scope: 'all' },
    },
  };
};

export const publicClipsFetcher = async ({
  params,
  sortFieldName,
  shortId,
  parentId,
  sortDirection,
  cursor,
  collectionId,
}: {
  cursor: string | null;
  shortId: string;
  parentId: string;
  params: Partial<ClipsListOptions>;
  sortFieldName: SortField['name'];
  sortDirection: SortDirection;
  /** See `getCollectionScopedClipParams` — absent outside a collection sub-page drill. */
  collectionId?: string;
}) => {
  const response = await ShortUrl.searchClipsByShortId(shortId, parentId, {
    cursor,
    ...getCollectionScopedClipParams({ params, collectionId }),
    limit: !cursor ? PageLimits.clips.init : PageLimits.clips.more,
    sortField: {
      direction: sortDirection,
      name: sortFieldName,
    },
  });

  return response;
};

export const privateFilesFetcher = async ({
  params,
  sortFieldName,
  sortDirection,
  cursor,
  workspaceId,
}: {
  cursor: string | null;
  params: Partial<ClipsListOptions>;
  sortFieldName: SortField['name'];
  sortDirection: SortDirection;
  workspaceId: string;
}) => {
  const response = await Clips.searchFiles({
    ...params,
    cursor,
    limit: !cursor ? PageLimits.clips.init : PageLimits.clips.more,
    sortField: {
      direction: sortDirection,
      name: sortFieldName,
    },
    workspaceId,
  });

  return response;
};

export const publicFilesFetcher = async ({
  params,
  sortFieldName,
  sortDirection,
  parentId,
  shortId,
  cursor,
  collectionId,
}: {
  cursor: string | null;
  shortId: string;
  parentId: string;
  params: Partial<ClipsListOptions>;
  sortFieldName: SortField['name'];
  sortDirection: SortDirection;
  /** See `getCollectionScopedClipParams` — absent outside a collection sub-page drill. */
  collectionId?: string;
}) => {
  const response = await ShortUrl.searchFilesByShortId(shortId, parentId, {
    ...getCollectionScopedClipParams({ params, collectionId }),
    cursor,
    limit: !cursor ? PageLimits.clips.init : PageLimits.clips.more,
    sortField: {
      direction: sortDirection,
      name: sortFieldName,
    },
  });

  return response;
};

export const getBoardsFromBoardsData = <B extends PublicBoard | Board, C extends Clip | PublicClip>(
  data?: ListResponse<B>[],
  boardIdsToExclude: string[] = [],
): B[] =>
  data
    ? data.reduce(
        (acc, curr) => {
          if (boardIdsToExclude.length === 0) {
            acc.push(...curr.data);
          } else {
            acc.push(...curr.data.filter((board) => !boardIdsToExclude.includes(board.id)));
          }

          return acc;
        },
        [] as UseGalleryViewItemsReturn<B, C>['data']['boards']['items'],
      )
    : [];

export const getAssetFromAssetsData = <C extends Clip | PublicClip>(data?: ClipsListResponse<C>[]) =>
  data
    ? data.reduce(
        (acc, curr) => {
          const assets = curr.data.clips;
          acc.push(...assets);
          return acc;
        },
        [] as UseGalleryViewItemsReturn<undefined, C>['data']['clips']['items'],
      )
    : [];

export const galleryViewDataToUpdateMediaPlayer = <B extends PublicBoard | Board, C extends Clip | PublicClip>(
  itemsData: UseGalleryViewItemsReturn<B, C>,
) => ({
  itemsIds: [...itemsData.data.clips.items, ...itemsData.data.files.items].map(({ id, assetId }) => ({
    assetId,
    id,
  })),
  loadMore: itemsData.loadNextPage,
  hasMore: itemsData.data.boards.hasMore || itemsData.data.clips.hasMore || itemsData.data.files.hasMore,
});
